import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useGlobalStore } from '@client/store/globalStore/context/globalContext';
import i18n from '@client/utils/i18n';
import lediumApi from '@client/utils/lediumApi';
import { type DeviceSettings } from '@ledium/types';
import { BlockingOverlay, TitleAndDescription } from '@ledium/ui-common';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import { Alert, Container, Grid, Tab, Tabs } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import ApplicationTab from './tabs/ApplicationTab';
import BackupTab from './tabs/BackupTab';
import SystemTab from './tabs/SystemTab';
import UserTab from './tabs/UserTab';

type SettingsRouteParams = Record<string, string | undefined> & {
    tabName?: string;
};

// exported for testing — window.location is non-configurable in vmThreads
export const _nav = { reload: () => window.location.assign('') };

const validTabs = ['application', 'backup', 'user', 'system'] as const;

function Settings() {
    const {
        globalState: { isOnline }
    } = useGlobalStore();

    const [offlineMode, setOfflineMode] = React.useState(false);
    const [helperAvailable, setHelperAvailable] = React.useState<boolean | null>(null);
    const [serialNumber, setSerialNumber] = React.useState('');
    const [blockingText, setBlockingText] = React.useState<string | null>(null);
    const [initialNightlyBuild, setInitialNightlyBuild] = React.useState(false);
    const [initialEnableNightlyBuildInstalls, setInitialEnableNightlyBuildInstalls] = React.useState(false);
    const [initialCloudBackupEnabled, setInitialCloudBackupEnabled] = React.useState(false);
    const [initialDeviceName, setInitialDeviceName] = React.useState('');

    const navigate = useNavigate();
    const params = useParams<SettingsRouteParams>();
    const activeTab = (validTabs.includes(params.tabName as (typeof validTabs)[number]) ? params.tabName : 'application') as (typeof validTabs)[number];

    React.useEffect(() => {
        lediumApi.get<{ available: boolean }>('settings/helperAvailable').then((data) => {
            setHelperAvailable(data?.available ?? false);
        });
    }, []);

    React.useEffect(() => {
        lediumApi.get<DeviceSettings>('settings/').then((data) => {
            if (data) {
                setInitialDeviceName(data.deviceName ?? '');
                setInitialCloudBackupEnabled(data.cloudBackupEnabled ?? false);
                setOfflineMode(data.offlineMode ?? false);
                setInitialNightlyBuild(data.nightlyBuild ?? false);
                setInitialEnableNightlyBuildInstalls(data.enableNightlyBuildInstalls ?? false);
                setSerialNumber(data.serialNumber ?? '');
            }
        });
    }, []);

    const helperUnavailable = helperAvailable === false;
    const serverOffline = !isOnline || offlineMode;

    return (
        <>
            <BlockingOverlay text={blockingText} />
            <Container sx={{ paddingBottom: '20px' }}>
                <Grid container spacing={3}>
                    <TitleAndDescription />

                    {serialNumber && (
                        <Grid item xs={12}>
                            <Typography variant="subtitle1">
                                {i18n('serial_number')}: <strong>{serialNumber}</strong>
                            </Typography>
                        </Grid>
                    )}

                    {(serverOffline || helperUnavailable) && (
                        <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                            {serverOffline && (
                                <Alert severity="error" icon={<WifiOffIcon fontSize="inherit" />}>
                                    {i18n('server_offline_warning')}
                                </Alert>
                            )}
                            {helperUnavailable && <Alert severity="warning">{i18n('helper_unavailable_warning')}</Alert>}
                        </Grid>
                    )}
                </Grid>

                <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
                    <Tabs value={activeTab} onChange={(_, value) => navigate(`/settings/${value}`)}>
                        <Tab label={i18n('tab_application')} value="application" />
                        <Tab label={i18n('tab_backup')} value="backup" />
                        <Tab label={i18n('tab_user')} value="user" />
                        <Tab label={i18n('tab_system')} value="system" />
                    </Tabs>
                </Box>

                {activeTab === 'application' && (
                    <ApplicationTab
                        helperUnavailable={helperUnavailable}
                        serverOffline={serverOffline}
                        initialNightlyBuild={initialNightlyBuild}
                        initialEnableNightlyBuildInstalls={initialEnableNightlyBuildInstalls}
                        onBlock={setBlockingText}
                    />
                )}

                {activeTab === 'backup' && (
                    <BackupTab serverOffline={serverOffline} initialCloudBackupEnabled={initialCloudBackupEnabled} onBlock={setBlockingText} />
                )}

                {activeTab === 'user' && <UserTab />}

                {activeTab === 'system' && (
                    <SystemTab
                        helperUnavailable={helperUnavailable}
                        isOnline={!!isOnline}
                        offlineMode={offlineMode}
                        initialDeviceName={initialDeviceName}
                        onOfflineModeChange={setOfflineMode}
                        onBlock={setBlockingText}
                    />
                )}
            </Container>
        </>
    );
}

export default Settings;
